import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { CartProvider } from './lib/cart';
import { TableOrderProvider } from './lib/table-context';
import { KitchenPauseProvider } from './lib/kitchen-pause-context';
import { LanguageProvider } from './lib/i18n';
import { KitchenPausedBanner } from './components/restaurant/KitchenPausedBanner';
import { getSubdomainSlug } from './lib/subdomain';
// Customer Pages
import { HomePage } from './pages/HomePage';
import { RestaurantListingPage } from './pages/RestaurantListingPage';
import { RestaurantPage } from './pages/RestaurantPage';
import { MenuPage } from './pages/MenuPage';
import { CartPage } from './pages/CartPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { OrderConfirmationPage } from './pages/OrderConfirmationPage';
import { TrackingPage } from './pages/TrackingPage';
import { TablePage } from './pages/TablePage';
// Admin Pages
import { LoginPage } from './pages/admin/LoginPage';
import { NoizaMetricsPage } from './pages/admin/NoizaMetricsPage';
import { DashboardPage } from './pages/admin/DashboardPage';
import { KDSPage } from './pages/admin/KDSPage';
import { InventoryPage } from './pages/admin/InventoryPage';
import { MetricsPage } from './pages/admin/MetricsPage';
import { MenuManagementPage } from './pages/admin/MenuManagementPage';
import { ImportPage } from './pages/admin/ImportPage';
import { StationSelectPage } from './pages/admin/StationSelectPage';
import { CajaOverviewPage } from './pages/admin/CajaOverviewPage';
import { CancelledOrdersPage } from './pages/admin/CancelledOrdersPage';
import { TableManagementPage } from './pages/admin/TableManagementPage';
import { TableQRPage } from './pages/admin/TableQRPage';
import { PublicMenuPage } from './pages/PublicMenuPage';
import { LoyaltyDashboardPage } from './pages/admin/LoyaltyDashboardPage';
import { LoyaltyRedemptionPage } from './pages/admin/LoyaltyRedemptionPage';
import { CreateRestaurantPage } from './pages/admin/CreateRestaurantPage';
import { DriversListPage } from './pages/admin/DriversListPage';
import { CreateDriverPage } from './pages/admin/CreateDriverPage';
import { VehiclesListPage } from './pages/admin/VehiclesListPage';
import { CreateVehiclePage } from './pages/admin/CreateVehiclePage';
import { AssignmentsPage } from './pages/admin/AssignmentsPage';
import { UsersAdminPage } from './pages/admin/UsersAdminPage';
import { EnrollmentPage } from './pages/admin/EnrollmentPage';
import { EnrollmentRestaurantPage } from './pages/admin/EnrollmentRestaurantPage';
import { EnrollmentMenuUpdatePage } from './pages/admin/EnrollmentMenuUpdatePage';
import { EnrollmentPlatformAdminPage } from './pages/admin/EnrollmentPlatformAdminPage';
import { EnrollmentMenuImagesPage } from './pages/admin/EnrollmentMenuImagesPage';
import { GlobalAdminLoginPage } from './pages/admin/GlobalAdminLoginPage';
import { RequireAdminAuth } from './components/admin/RequireAdminAuth';
import { RequirePlatformAdmin } from './components/admin/RequirePlatformAdmin';
import { OnboardingPage } from './pages/OnboardingPage';
// Driver App
import { DriverLoginPage } from './pages/driver/DriverLoginPage';
import { DriverAppPage } from './pages/driver/DriverAppPage';
// Tenant Access
import { TenantProvider } from './lib/tenant-context';
// ─── MAINTENANCE GATE ───
// Slugs under maintenance — only accessible with ?dev=noiza2026
const MAINTENANCE_SLUGS = ['noiza.restaurant'];
function MaintenancePage() {
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);
  const handleUnlock = () => {
    if (code === 'noiza2026') {
      sessionStorage.setItem('noiza_dev_access', 'true');
      window.location.reload();
    } else {
      setError(true);
    }
  };
  return <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center p-6" data-id="element-0">
      <div className="max-w-md w-full text-center" data-id="element-1">
        <div className="w-16 h-16 mx-auto mb-6 bg-[#141414] rounded-2xl flex items-center justify-center border border-white/10" data-id="element-2">
          <span className="text-2xl" data-id="element-3">🔧</span>
        </div>
        <h1 className="text-2xl font-black text-white mb-2" data-id="element-4">
          En Mantenimiento
        </h1>
        <p className="text-white/40 text-sm mb-8" data-id="element-5">
          Este restaurante está temporalmente en modo de pruebas. Volveremos
          pronto.
        </p>
        <div className="space-y-3" data-id="element-6">
          <input type="password" placeholder="Código de acceso" value={code} onChange={e => {
          setCode(e.target.value);
          setError(false);
        }} onKeyDown={e => e.key === 'Enter' && handleUnlock()} className="w-full px-4 py-3 rounded-xl bg-[#141414] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#006847] focus:border-transparent text-center" data-id="element-7" />
          {error && <p className="text-[#CE1126] text-xs" data-id="element-8">Código incorrecto</p>}
          <button onClick={handleUnlock} className="w-full py-3 rounded-xl bg-[#006847] text-white font-bold text-sm hover:bg-[#005a3d] transition-colors" data-id="element-9">
            Acceder
          </button>
        </div>
      </div>
    </div>;
}
/** Routes Capricho de Canela to PublicMenuPage (premium theme), others to RestaurantPage */
function SmartRestaurantPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  // Maintenance gate: check URL param or session
  if (slug && MAINTENANCE_SLUGS.includes(slug)) {
    const urlParams = new URLSearchParams(window.location.search);
    const hasDevParam = urlParams.get('dev') === 'noiza2026';
    const hasSessionAccess = sessionStorage.getItem('noiza_dev_access') === 'true';
    if (hasDevParam && !hasSessionAccess) {
      sessionStorage.setItem('noiza_dev_access', 'true');
    }
    if (!hasDevParam && !hasSessionAccess) {
      return <MaintenancePage data-id="element-10" />;
    }
  }
  if (slug === 'capricho-de-canela') {
    return <PublicMenuPage data-id="element-11" />;
  }
  return <RestaurantPage data-id="element-12" />;
}
/** Redirects /admin/:slug → /admin/:slug/operacion (station select) */
function AdminRedirect() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  return <Navigate to={`/admin/${slug}/operacion`} replace data-id="element-13" />;
}
/**
 * Subdomain-aware admin redirect.
 * When on a tenant subdomain, /admin → /admin/dashboard
 */
function SubdomainAdminRedirect({
  slug
}: {
  slug: string;
}) {
  return <Navigate to="/admin/operacion" replace data-id="element-14" />;
}
/**
 * Wraps TenantProvider to inject the subdomain slug
 * when there's no :slug in the path params.
 */
function SubdomainTenantProvider({
  children,
  slug
}: {
  children: React.ReactNode;
  slug: string;
}) {
  // We pass the slug through a context-compatible wrapper
  // TenantProvider reads from useParams, but we need to override it.
  // Instead, we'll use the updated TenantProvider that accepts an optional slug prop.
  return <TenantProvider subdomainSlug={slug} data-id="element-15">{children}</TenantProvider>;
}
/**
 * Main routing component.
 * Detects subdomain and renders appropriate routes.
 */
function AppRoutes() {
  const subdomainSlug = getSubdomainSlug();
  // ─── SUBDOMAIN MODE ───
  // When accessed via {slug}.noiza.estrategiasdigitalesaz.com
  if (subdomainSlug) {
    return <Routes data-id="element-16">
        {/* Public restaurant page at root */}
        <Route path="/" element={<PublicMenuPage subdomainSlug={subdomainSlug} data-id="element-18" />} data-id="element-17" />

        {/* Cart & checkout still work */}
        <Route path="/cart" element={<CartPage data-id="element-20" />} data-id="element-19" />
        <Route path="/checkout" element={<CheckoutPage data-id="element-22" />} data-id="element-21" />
        <Route path="/order-confirmation" element={<OrderConfirmationPage data-id="element-24" />} data-id="element-23" />
        <Route path="/track/:trackingToken" element={<TrackingPage data-id="element-26" />} data-id="element-25" />

        {/* Table ordering */}
        <Route path="/table/:tableNumber" element={<TablePage data-id="element-28" />} data-id="element-27" />
        <Route path="/mesa/:tableNumber/:token" element={<TablePage data-id="element-30" />} data-id="element-29" />

        {/* Admin routes — no :slug needed in path, resolved from subdomain */}
        <Route path="/admin/login" element={<LoginPage subdomainSlug={subdomainSlug} data-id="element-32" />} data-id="element-31" />
        <Route path="/admin" element={<SubdomainAdminRedirect slug={subdomainSlug} data-id="element-34" />} data-id="element-33" />
        <Route path="/admin/operacion" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-36">
              <StationSelectPage data-id="element-37" />
            </SubdomainTenantProvider>} data-id="element-35" />
        <Route path="/admin/overview" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-39">
              <CajaOverviewPage data-id="element-40" />
            </SubdomainTenantProvider>} data-id="element-38" />
        <Route path="/admin/dashboard" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-42">
              <DashboardPage data-id="element-43" />
            </SubdomainTenantProvider>} data-id="element-41" />
        <Route path="/admin/caja" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-45">
              <CajaOverviewPage data-id="element-46" />
            </SubdomainTenantProvider>} data-id="element-44" />
        <Route path="/admin/mesas" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-48">
              <TableManagementPage data-id="element-49" />
            </SubdomainTenantProvider>} data-id="element-47" />
        <Route path="/admin/mesas/qr" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-51">
              <TableQRPage data-id="element-52" />
            </SubdomainTenantProvider>} data-id="element-50" />

        <Route path="/admin/kds" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-54">
              <KDSPage data-id="element-55" />
            </SubdomainTenantProvider>} data-id="element-53" />
        <Route path="/admin/menu" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-57">
              <MenuManagementPage data-id="element-58" />
            </SubdomainTenantProvider>} data-id="element-56" />
        <Route path="/admin/inventory" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-60">
              <InventoryPage data-id="element-61" />
            </SubdomainTenantProvider>} data-id="element-59" />
        <Route path="/admin/metrics" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-63">
              <MetricsPage data-id="element-64" />
            </SubdomainTenantProvider>} data-id="element-62" />
        <Route path="/admin/loyalty" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-66">
              <LoyaltyDashboardPage data-id="element-67" />
            </SubdomainTenantProvider>} data-id="element-65" />
        <Route path="/admin/loyalty/redeem" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-69">
              <LoyaltyRedemptionPage data-id="element-70" />
            </SubdomainTenantProvider>} data-id="element-68" />

        {/* Cancelled orders (subdomain mode) */}
        <Route path="/admin/cancelados" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-72">
              <CancelledOrdersPage data-id="element-73" />
            </SubdomainTenantProvider>} data-id="element-71" />

        {/* Add import route for subdomain mode */}
        <Route path="/admin/import" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-75">
              <ImportPage data-id="element-76" />
            </SubdomainTenantProvider>} data-id="element-74" />

        {/* Global admin login */}
        <Route path="/admin/login" element={<GlobalAdminLoginPage data-id="element-78" />} data-id="element-77" />

        {/* NOIZA Platform Metrics (global, not tenant-specific) */}
        <Route path="/admin/noiza/metrics" element={<RequirePlatformAdmin data-id="element-80">
              <NoizaMetricsPage data-id="element-81" />
            </RequirePlatformAdmin>} data-id="element-79" />

        {/* Global admin tools — all require authentication */}
        <Route path="/admin/restaurants/new" element={<RequireAdminAuth data-id="element-83">
              <CreateRestaurantPage data-id="element-84" />
            </RequireAdminAuth>} data-id="element-82" />
        <Route path="/admin/drivers" element={<RequireAdminAuth data-id="element-86">
              <DriversListPage data-id="element-87" />
            </RequireAdminAuth>} data-id="element-85" />
        <Route path="/admin/drivers/new" element={<RequirePlatformAdmin data-id="element-89">
              <CreateDriverPage data-id="element-90" />
            </RequirePlatformAdmin>} data-id="element-88" />
        <Route path="/admin/vehicles" element={<RequireAdminAuth data-id="element-92">
              <VehiclesListPage data-id="element-93" />
            </RequireAdminAuth>} data-id="element-91" />
        <Route path="/admin/vehicles/new" element={<RequireAdminAuth data-id="element-95">
              <CreateVehiclePage data-id="element-96" />
            </RequireAdminAuth>} data-id="element-94" />
        <Route path="/admin/assignments" element={<RequireAdminAuth data-id="element-98">
              <AssignmentsPage data-id="element-99" />
            </RequireAdminAuth>} data-id="element-97" />
        <Route path="/admin/users" element={<RequireAdminAuth data-id="element-101">
              <UsersAdminPage data-id="element-102" />
            </RequireAdminAuth>} data-id="element-100" />
        <Route path="/admin/enrollment" element={<RequirePlatformAdmin data-id="element-104">
              <EnrollmentPage data-id="element-105" />
            </RequirePlatformAdmin>} data-id="element-103" />
        <Route path="/admin/enrollment/driver" element={<Navigate to="/admin/drivers/new" replace data-id="element-107" />} data-id="element-106" />
        <Route path="/admin/enrollment/restaurant" element={<RequirePlatformAdmin data-id="element-109">
              <EnrollmentRestaurantPage data-id="element-110" />
            </RequirePlatformAdmin>} data-id="element-108" />
        <Route path="/admin/enrollment/menu-update" element={<RequirePlatformAdmin data-id="element-112">
              <EnrollmentMenuUpdatePage data-id="element-113" />
            </RequirePlatformAdmin>} data-id="element-111" />
        <Route path="/admin/enrollment/platform-admin" element={<RequirePlatformAdmin data-id="element-115">
              <EnrollmentPlatformAdminPage data-id="element-116" />
            </RequirePlatformAdmin>} data-id="element-114" />
        <Route path="/admin/enrollment/menu-images" element={<RequirePlatformAdmin data-id="element-118">
              <EnrollmentMenuImagesPage data-id="element-119" />
            </RequirePlatformAdmin>} data-id="element-117" />
        <Route path="/admin/enrollment/metrics" element={<RequirePlatformAdmin data-id="element-121">
              <NoizaMetricsPage data-id="element-122" />
            </RequirePlatformAdmin>} data-id="element-120" />

        {/* Fallback: also support legacy path-based admin on subdomain */}
        <Route path="/admin/:slug/login" element={<LoginPage data-id="element-124" />} data-id="element-123" />
        <Route path="/admin/:slug" element={<AdminRedirect data-id="element-126" />} data-id="element-125" />
        <Route path="/admin/:slug/operacion" element={<TenantProvider data-id="element-128">
              <StationSelectPage data-id="element-129" />
            </TenantProvider>} data-id="element-127" />
        <Route path="/admin/:slug/dashboard" element={<TenantProvider data-id="element-131">
              <DashboardPage data-id="element-132" />
            </TenantProvider>} data-id="element-130" />
        <Route path="/admin/:slug/caja" element={<TenantProvider data-id="element-134">
              <CajaOverviewPage data-id="element-135" />
            </TenantProvider>} data-id="element-133" />
        <Route path="/admin/:slug/mesas" element={<TenantProvider data-id="element-137">
              <TableManagementPage data-id="element-138" />
            </TenantProvider>} data-id="element-136" />
        <Route path="/admin/:slug/mesas/qr" element={<TenantProvider data-id="element-140">
              <TableQRPage data-id="element-141" />
            </TenantProvider>} data-id="element-139" />
        <Route path="/admin/:slug/cancelados" element={<TenantProvider data-id="element-143">
              <CancelledOrdersPage data-id="element-144" />
            </TenantProvider>} data-id="element-142" />
        <Route path="/admin/:slug/kds" element={<TenantProvider data-id="element-146">
              <KDSPage data-id="element-147" />
            </TenantProvider>} data-id="element-145" />
        <Route path="/admin/:slug/menu" element={<TenantProvider data-id="element-149">
              <MenuManagementPage data-id="element-150" />
            </TenantProvider>} data-id="element-148" />
        <Route path="/admin/:slug/inventory" element={<TenantProvider data-id="element-152">
              <InventoryPage data-id="element-153" />
            </TenantProvider>} data-id="element-151" />
        <Route path="/admin/:slug/metrics" element={<TenantProvider data-id="element-155">
              <MetricsPage data-id="element-156" />
            </TenantProvider>} data-id="element-154" />
        <Route path="/admin/:slug/loyalty" element={<TenantProvider data-id="element-158">
              <LoyaltyDashboardPage data-id="element-159" />
            </TenantProvider>} data-id="element-157" />
        <Route path="/admin/:slug/loyalty/redeem" element={<TenantProvider data-id="element-161">
              <LoyaltyRedemptionPage data-id="element-162" />
            </TenantProvider>} data-id="element-160" />

        {/* Add legacy import route on subdomain */}
        <Route path="/admin/:slug/import" element={<TenantProvider data-id="element-164">
              <ImportPage data-id="element-165" />
            </TenantProvider>} data-id="element-163" />

        {/* Catch-all: any other path on subdomain → public menu */}
        <Route path="*" element={<PublicMenuPage subdomainSlug={subdomainSlug} data-id="element-167" />} data-id="element-166" />
      </Routes>;
  }
  // ─── STANDARD PATH-BASED MODE ───
  // When accessed via noiza.estrategiasdigitalesaz.com/:slug
  return <Routes data-id="element-168">
      {/* Customer Facing */}
      <Route path="/" element={<HomePage data-id="element-170" />} data-id="element-169" />
      <Route path="/aplicar" element={<OnboardingPage data-id="element-172" />} data-id="element-171" />
      <Route path="/restaurants" element={<RestaurantListingPage data-id="element-174" />} data-id="element-173" />
      <Route path="/r/:slug" element={<SmartRestaurantPage data-id="element-176" />} data-id="element-175" />
      <Route path="/r/:slug/menu" element={<MenuPage data-id="element-178" />} data-id="element-177" />
      <Route path="/r/:slug/table/:tableNumber" element={<TablePage data-id="element-180" />} data-id="element-179" />
      <Route path="/r/:slug/mesa/:tableNumber/:token" element={<TablePage data-id="element-182" />} data-id="element-181" />
      <Route path="/cart" element={<CartPage data-id="element-184" />} data-id="element-183" />
      <Route path="/checkout" element={<CheckoutPage data-id="element-186" />} data-id="element-185" />
      <Route path="/order-confirmation" element={<OrderConfirmationPage data-id="element-188" />} data-id="element-187" />
      <Route path="/track/:trackingToken" element={<TrackingPage data-id="element-190" />} data-id="element-189" />

      {/* Global admin login */}
      <Route path="/admin/login" element={<GlobalAdminLoginPage data-id="element-192" />} data-id="element-191" />

      {/* NOIZA Platform Metrics (global, not tenant-specific) */}
      <Route path="/admin/noiza/metrics" element={<RequirePlatformAdmin data-id="element-194">
            <NoizaMetricsPage data-id="element-195" />
          </RequirePlatformAdmin>} data-id="element-193" />

      {/* Global admin tools — all require authentication */}
      <Route path="/admin/restaurants/new" element={<RequireAdminAuth data-id="element-197">
            <CreateRestaurantPage data-id="element-198" />
          </RequireAdminAuth>} data-id="element-196" />
      <Route path="/admin/drivers" element={<RequireAdminAuth data-id="element-200">
            <DriversListPage data-id="element-201" />
          </RequireAdminAuth>} data-id="element-199" />
      <Route path="/admin/drivers/new" element={<RequirePlatformAdmin data-id="element-203">
            <CreateDriverPage data-id="element-204" />
          </RequirePlatformAdmin>} data-id="element-202" />
      <Route path="/admin/vehicles" element={<RequireAdminAuth data-id="element-206">
            <VehiclesListPage data-id="element-207" />
          </RequireAdminAuth>} data-id="element-205" />
      <Route path="/admin/vehicles/new" element={<RequireAdminAuth data-id="element-209">
            <CreateVehiclePage data-id="element-210" />
          </RequireAdminAuth>} data-id="element-208" />
      <Route path="/admin/assignments" element={<RequireAdminAuth data-id="element-212">
            <AssignmentsPage data-id="element-213" />
          </RequireAdminAuth>} data-id="element-211" />
      <Route path="/admin/users" element={<RequireAdminAuth data-id="element-215">
            <UsersAdminPage data-id="element-216" />
          </RequireAdminAuth>} data-id="element-214" />
      <Route path="/admin/enrollment" element={<RequirePlatformAdmin data-id="element-218">
            <EnrollmentPage data-id="element-219" />
          </RequirePlatformAdmin>} data-id="element-217" />
      <Route path="/admin/enrollment/driver" element={<Navigate to="/admin/drivers/new" replace data-id="element-221" />} data-id="element-220" />
      <Route path="/admin/enrollment/restaurant" element={<RequirePlatformAdmin data-id="element-223">
            <EnrollmentRestaurantPage data-id="element-224" />
          </RequirePlatformAdmin>} data-id="element-222" />
      <Route path="/admin/enrollment/menu-update" element={<RequirePlatformAdmin data-id="element-226">
            <EnrollmentMenuUpdatePage data-id="element-227" />
          </RequirePlatformAdmin>} data-id="element-225" />
      <Route path="/admin/enrollment/platform-admin" element={<RequirePlatformAdmin data-id="element-229">
            <EnrollmentPlatformAdminPage data-id="element-230" />
          </RequirePlatformAdmin>} data-id="element-228" />
      <Route path="/admin/enrollment/menu-images" element={<RequirePlatformAdmin data-id="element-232">
            <EnrollmentMenuImagesPage data-id="element-233" />
          </RequirePlatformAdmin>} data-id="element-231" />
      <Route path="/admin/enrollment/metrics" element={<RequirePlatformAdmin data-id="element-235">
            <NoizaMetricsPage data-id="element-236" />
          </RequirePlatformAdmin>} data-id="element-234" />

      {/* Driver App */}
      <Route path="/driver/login" element={<DriverLoginPage data-id="element-238" />} data-id="element-237" />
      <Route path="/driver/app" element={<DriverAppPage data-id="element-240" />} data-id="element-239" />

      {/* Admin - Login */}
      <Route path="/admin/:slug/login" element={<LoginPage data-id="element-242" />} data-id="element-241" />

      {/* Admin - Protected by TenantProvider */}
      <Route path="/admin/:slug" element={<AdminRedirect data-id="element-244" />} data-id="element-243" />
      <Route path="/admin/:slug/operacion" element={<TenantProvider data-id="element-246">
            <StationSelectPage data-id="element-247" />
          </TenantProvider>} data-id="element-245" />
      <Route path="/admin/:slug/dashboard" element={<TenantProvider data-id="element-249">
            <DashboardPage data-id="element-250" />
          </TenantProvider>} data-id="element-248" />
      <Route path="/admin/:slug/caja" element={<TenantProvider data-id="element-252">
            <CajaOverviewPage data-id="element-253" />
          </TenantProvider>} data-id="element-251" />
      <Route path="/admin/:slug/mesas" element={<TenantProvider data-id="element-255">
            <TableManagementPage data-id="element-256" />
          </TenantProvider>} data-id="element-254" />
      <Route path="/admin/:slug/mesas/qr" element={<TenantProvider data-id="element-258">
            <TableQRPage data-id="element-259" />
          </TenantProvider>} data-id="element-257" />
      <Route path="/admin/:slug/kds" element={<TenantProvider data-id="element-261">
            <KDSPage data-id="element-262" />
          </TenantProvider>} data-id="element-260" />
      <Route path="/admin/:slug/menu" element={<TenantProvider data-id="element-264">
            <MenuManagementPage data-id="element-265" />
          </TenantProvider>} data-id="element-263" />
      <Route path="/admin/:slug/inventory" element={<TenantProvider data-id="element-267">
            <InventoryPage data-id="element-268" />
          </TenantProvider>} data-id="element-266" />
      <Route path="/admin/:slug/metrics" element={<TenantProvider data-id="element-270">
            <MetricsPage data-id="element-271" />
          </TenantProvider>} data-id="element-269" />
      <Route path="/admin/:slug/loyalty" element={<TenantProvider data-id="element-273">
            <LoyaltyDashboardPage data-id="element-274" />
          </TenantProvider>} data-id="element-272" />
      <Route path="/admin/:slug/loyalty/redeem" element={<TenantProvider data-id="element-276">
            <LoyaltyRedemptionPage data-id="element-277" />
          </TenantProvider>} data-id="element-275" />

      {/* Cancelled orders */}
      <Route path="/admin/:slug/cancelados" element={<TenantProvider data-id="element-279">
            <CancelledOrdersPage data-id="element-280" />
          </TenantProvider>} data-id="element-278" />

      {/* Add import route for path-based mode */}
      <Route path="/admin/:slug/import" element={<TenantProvider data-id="element-282">
            <ImportPage data-id="element-283" />
          </TenantProvider>} data-id="element-281" />

      {/* Public Restaurant Page (Must be last) */}
      <Route path="/:slug" element={<SmartRestaurantPage data-id="element-285" />} data-id="element-284" />

      {/* Catch-all route */}
      <Route path="*" element={<Navigate to="/" replace data-id="element-287" />} data-id="element-286" />
    </Routes>;
}
export function App() {
  return <LanguageProvider data-id="element-288">
      <CartProvider data-id="element-289">
        <TableOrderProvider data-id="element-290">
          <KitchenPauseProvider data-id="element-291">
            <BrowserRouter data-id="element-292">
              <KitchenPausedBanner data-id="element-293" />
              <div className="min-h-screen flex flex-col bg-brand-offwhite font-sans text-gray-900" data-id="element-294">
                <AppRoutes data-id="element-295" />
              </div>
            </BrowserRouter>
          </KitchenPauseProvider>
        </TableOrderProvider>
      </CartProvider>
    </LanguageProvider>;
}